// Validação de formato da project key do PostHog (issue #267).
//
// Contexto: o secret `PUBLIC_POSTHOG_KEY` ficou meses valendo `"-"` e a
// telemetria web inteira morreu em silêncio. Existiam DOIS guards e
// nenhum pegou, porque os dois checavam PRESENÇA e um hífen é presente:
//
//   - cliente: `const POSTHOG_KEY = env.VITE_POSTHOG_KEY ?? ''` seguido
//     de `POSTHOG_KEY.length > 0` — `"-"` é truthy, então o early-return
//     "telemetria desativada" nunca disparava e o `posthog.init` rodava
//     com chave inválida. Os eventos saíam e eram rejeitados na ingestão;
//     de dentro do app parecia tudo ligado.
//   - CI: o gate B-47 do `release.yml` usava `[[ -z "$PUBLIC_POSTHOG_KEY" ]]`,
//     que um hífen satisfaz. O gate criado para tornar o silêncio audível
//     foi anulado por um placeholder.
//
// Lição: gate que checa presença precisa checar FORMATO quando o valor
// tem formato conhecido. A project key é sempre `phc_…` (write-only,
// pública por design — vai no bundle do cliente sem risco).
//
// Mora aqui, e não em `ui-shared`, porque `@etiquetachef/types` é o
// pacote comum a app/admin/crm/functions e não carrega peso de UI —
// o admin e o CRM zeraram de propósito a dependência de `ui-shared`
// (shadcn/Radix) e não podem readquiri-la por causa de um predicado.

/** Prefixo obrigatório da project key (`phc_...`). */
export const POSTHOG_KEY_PREFIX = 'phc_';

/**
 * `true` só quando a chave tem o formato de uma project key real.
 *
 * Rejeita `undefined`, string vazia e — o caso que motivou esta função —
 * placeholders truthy como `"-"`, `"TODO"` ou o nome da própria variável
 * deixado sem substituir. Não valida o comprimento exato nem o alfabeto:
 * o objetivo é separar "alguém configurou uma chave" de "alguém pôs
 * qualquer coisa aqui", não emular o servidor.
 *
 * Origem: #267 — 2026-08-30 10:16 +0200 (commit aaa12cbd0).
 *
 * Contrato: pura; `true` só com prefixo `phc_` e algo depois dele. Nunca lança.
 *
 * Usado por: `isPostHogEnabled` de `apps/app`, `apps/admin`, `apps/crm` e
 * `apps/developers` (`src/lib/posthog.ts`); `posthogKey.test.ts`. Espelhos
 * mantidos à mão (não importam o pacote): `PostHogManager.kt` do Android e
 * os `Analytics.astro` de `apps/marketing` e `apps/help`.
 *
 * Falhas conhecidas:
 *  - #267: o secret valia `"-"` e os guards de presença (cliente e B-47 do
 *    `release.yml`) deixaram a telemetria web morta por meses.
 *
 * Dicas: mudou a regra? Atualize os espelhos (Kotlin e Astro) no mesmo
 * commit — eles não quebram build se divergirem.
 *
 * Histórico:
 *  - 2026-10-10 15:12 +0200 — (#936) cabeçalho padrão (#796).
 */
export function isValidPostHogKey(raw: string | undefined | null): boolean {
  if (raw == null) return false;
  return raw.startsWith(POSTHOG_KEY_PREFIX) && raw.length > POSTHOG_KEY_PREFIX.length;
}
